import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
  useToast
} from "@chakra-ui/react";
import {
  PiMagnifyingGlassDuotone as FiSearch,
  PiUserPlusDuotone as FiUserPlus,
  PiCheckBold as FiCheck,
  PiUsersThreeDuotone as FiUsers
} from "react-icons/pi";
import { observer } from "mobx-react-lite";
import { getUsersLookup, VoterLookupResult } from "@lubbezposrednio-frontend/core";
import { EligibleVotersModel } from "../EligibleVotersModel";
import { GLASS, drgba, EASE } from "../../../../../components/glassUi";

const C = "#7b2ff7";
const backendUrl = (p?: string | null) => (p ? `${process.env.REACT_APP_BACKEND_URL}${p}` : undefined);

type Props = { model: EligibleVotersModel; isOpen: boolean; onClose: () => void };

const inputStyle = {
  bg: "rgba(255,255,255,0.72)",
  borderRadius: "12px",
  borderWidth: "1.5px",
  borderColor: drgba("#430098", 0.22),
  transition: `all ${EASE}`,
  _hover: { borderColor: drgba(C, 0.45) },
  _focusVisible: { borderColor: C, boxShadow: `0 0 0 3px ${drgba(C, 0.16)}` }
};

export const AddVotersModal: React.FC<Props> = observer(({ model, isOpen, onClose }) => {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<VoterLookupResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [bulk, setBulk] = useState("");

  useEffect(() => {
    const query = q.trim();
    if (query.length < 3) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      getUsersLookup(query)
        .then(r => setResults(r.data.results))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const isAdded = (email: string) => model.votersEmails.some(e => e.value.toLowerCase() === email.toLowerCase());
  const bulkCount = bulk.split(/[\s,;]+/).filter(v => v.includes("@")).length;

  const addBulk = () => {
    const ok = model.handleAddEmail(bulk, toast);
    if (ok) {
      setBulk("");
      toast({ status: "success", isClosable: true, title: "Dodano adresy do listy uprawnionych" });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(24,12,48,0.45)" backdropFilter="blur(3px)" />
      <ModalContent borderRadius="20px" overflow="hidden" sx={GLASS} boxShadow={GLASS.boxShadow}>
        <ModalHeader color="#241536" fontWeight="800" bg={drgba(C, 0.06)} borderBottom={`1px solid ${drgba("#430098", 0.1)}`}>
          <HStack spacing="10px">
            <Flex w="34px" h="34px" borderRadius="10px" bg={drgba(C, 0.14)} align="center" justify="center">
              <Icon as={FiUserPlus} color={C} boxSize="18px" />
            </Flex>
            <Text>Dodaj uprawnionych</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="22px" pt="18px">
          <Tabs colorScheme="purple" variant="soft-rounded" isLazy>
            <TabList mb="16px" gap="8px">
              <Tab fontSize="13.5px" fontWeight="700" borderRadius="full">
                Wyszukaj konta
              </Tab>
              <Tab fontSize="13.5px" fontWeight="700" borderRadius="full">
                Wklej listę
              </Tab>
            </TabList>

            <TabPanels>
              {/* Wyszukiwanie kont (smart-search) */}
              <TabPanel p="0">
                <InputGroup>
                  <InputLeftElement h="48px">
                    <Icon as={FiSearch} color={drgba(C, 0.6)} boxSize="18px" />
                  </InputLeftElement>
                  <Input
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="Imię, nazwa, @ref lub dokładny e-mail…"
                    h="48px"
                    fontWeight="600"
                    color="#241536"
                    {...inputStyle}
                  />
                </InputGroup>
                <Text fontSize="12px" color="#a8a2b6" mt="6px">
                  Pokazujemy tylko konta, które zgodziły się na dodawanie. Po nazwie — wyłącznie profile publiczne; po pełnym e-mailu — dowolne.
                </Text>

                <Box mt="14px" minH="120px">
                  {loading ? (
                    <Flex justify="center" py="30px">
                      <Spinner color={C} thickness="2.5px" />
                    </Flex>
                  ) : q.trim().length < 3 ? (
                    <EmptyHint text="Wpisz co najmniej 3 znaki, aby wyszukać konta." />
                  ) : results && results.length === 0 ? (
                    <EmptyHint text="Brak pasujących kont. Możesz dodać adres w zakładce „Wklej listę”." />
                  ) : (
                    <VStack align="stretch" spacing="6px">
                      {results?.map(u => {
                        const added = isAdded(u.email);
                        return (
                          <HStack key={u.email} spacing="11px" p="9px 10px" borderRadius="12px" border={`1px solid ${drgba("#430098", 0.1)}`} bg="rgba(255,255,255,0.72)">
                            <Avatar size="sm" name={u.name} src={backendUrl(u.avatar_url)} bg={C} color="white" />
                            <Box flex="1" minW={0}>
                              <Text fontSize="14px" fontWeight="700" color="#241536" noOfLines={1}>
                                {u.name}
                              </Text>
                              <Text fontSize="12px" color="#a8a2b6" noOfLines={1}>
                                {u.email}
                              </Text>
                            </Box>
                            {added ? (
                              <HStack spacing="5px" color="#10b981" flexShrink={0} px="10px">
                                <Icon as={FiCheck} boxSize="15px" />
                                <Text fontSize="13px" fontWeight="700">
                                  Dodano
                                </Text>
                              </HStack>
                            ) : (
                              <Button size="sm" leftIcon={<Icon as={FiUserPlus} boxSize="15px" />} bg={C} color="white" borderRadius="10px" fontWeight="700" flexShrink={0} onClick={() => model.handleAddEmail(u.email, toast)} _hover={{ bg: "#6a1fe0" }}>
                                Dodaj
                              </Button>
                            )}
                          </HStack>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
              </TabPanel>

              {/* Wklej listę zbiorczo */}
              <TabPanel p="0">
                <Text fontSize="13px" color="#6b6480" mb="10px">
                  Wklej adresy e-mail — po jednym w wierszu lub oddzielone przecinkiem/spacją. Duplikaty i błędne adresy odfiltrujemy.
                </Text>
                <Textarea
                  value={bulk}
                  onChange={e => setBulk(e.target.value)}
                  placeholder={"jan.kowalski@example.com\nanna.nowak@example.com\n…"}
                  minH="160px"
                  fontSize="14px"
                  color="#241536"
                  {...inputStyle}
                />
                <Flex justify="space-between" align="center" mt="12px">
                  <Text fontSize="13px" color="#6b6480">
                    Wykryto adresów: <b>{bulkCount}</b>
                  </Text>
                  <Button bg={C} color="white" borderRadius="11px" fontWeight="700" isDisabled={bulkCount === 0} onClick={addBulk} _hover={{ bg: "#6a1fe0" }} _disabled={{ bg: drgba(C, 0.3), cursor: "not-allowed" }}>
                    Dodaj wszystkie
                  </Button>
                </Flex>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <HStack mt="18px" pt="14px" borderTop={`1px solid ${drgba("#430098", 0.1)}`} spacing="7px" color="#6b6480">
            <Icon as={FiUsers} boxSize="15px" />
            <Text fontSize="13px">
              Na liście uprawnionych: <b>{model.votersEmails.length}</b> (dodanych ręcznie/wyszukanych)
            </Text>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});

const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <Flex direction="column" align="center" justify="center" py="24px" color="#a8a2b6">
    <Icon as={FiSearch} boxSize="22px" mb="6px" color={drgba(C, 0.5)} />
    <Text fontSize="13px" textAlign="center" maxW="320px">
      {text}
    </Text>
  </Flex>
);
